/**
 * 法令サービス（実API実装）
 *
 * バックエンドAPI呼び出し:
 * - GET /api/laws - 法令検索
 * - GET /api/laws/:id - 法令詳細取得
 * - GET /api/laws/:id/articles - 法令条文取得
 * - GET /api/laws/bookmarks - ブックマーク一覧取得
 * - POST /api/laws/bookmarks - ブックマーク追加
 * - DELETE /api/laws/bookmarks/:id - ブックマーク削除
 */
import apiClient, { extractErrorMessage } from '../../lib/apiClient';
import type { Law, Bookmark, Country, PaginatedResponse } from '../../types';

// 法令カテゴリ型
export type LawCategory =
  | 'company'
  | 'financial'
  | 'antitrust'
  | 'labor'
  | 'tax'
  | 'foreign_exchange';

// 法令カテゴリラベル
export const LAW_CATEGORY_LABELS: Record<LawCategory, string> = {
  company: '会社法関連',
  financial: '金融商品取引法関連',
  antitrust: '独占禁止法関連',
  labor: '労働法関連',
  tax: '税法関連',
  foreign_exchange: '外為法関連',
};

// 法令条文型
export interface LawArticle {
  id: string;
  lawId: string;
  articleNumber: string;
  title?: string;
  content: string;
}

// バックエンドレスポンス型（snake_case）
interface BackendLawResponse {
  id: string;
  title: string;
  law_number: string;
  country: Country;
  category: string;
  effective_date: string;
  content?: string;
}

interface BackendLawArticleResponse {
  id: string;
  law_id: string;
  article_number: string;
  title?: string;
  content: string;
}

interface BackendBookmarkResponse {
  id: string;
  user_id: string;
  law_id: string;
  article_number?: string;
  created_at: string;
}

// 検索パラメータ型
export interface LawSearchParams {
  query?: string;
  category?: string;
  country?: Country;
  page?: number;
  pageSize?: number;
}

// 変換関数: snake_case -> camelCase
function convertLaw(law: BackendLawResponse): Law {
  return {
    id: law.id,
    title: law.title,
    lawNumber: law.law_number,
    country: law.country,
    category: law.category,
    effectiveDate: law.effective_date,
    content: law.content,
  };
}

function convertLawArticle(article: BackendLawArticleResponse): LawArticle {
  return {
    id: article.id,
    lawId: article.law_id,
    articleNumber: article.article_number,
    title: article.title,
    content: article.content,
  };
}

function convertBookmark(bookmark: BackendBookmarkResponse): Bookmark {
  return {
    id: bookmark.id,
    userId: bookmark.user_id,
    lawId: bookmark.law_id,
    articleNumber: bookmark.article_number,
    createdAt: bookmark.created_at,
  };
}

export const lawService = {
  /**
   * 法令一覧を検索・取得
   * GET /api/laws
   */
  async searchLaws(params?: LawSearchParams): Promise<Law[]> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.query) searchParams.append('query', params.query);
      if (params?.category) searchParams.append('category', params.category);
      if (params?.country) searchParams.append('country', params.country);
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.pageSize) searchParams.append('page_size', params.pageSize.toString());

      const queryString = searchParams.toString();
      const url = queryString ? `/api/laws?${queryString}` : '/api/laws';

      const response = await apiClient.get<{ data: BackendLawResponse[] }>(url);
      return response.data.data.map(convertLaw);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * 法令一覧を検索・取得（ページネーション対応）
   * GET /api/laws
   */
  async searchLawsPaginated(params?: LawSearchParams): Promise<PaginatedResponse<Law>> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.query) searchParams.append('query', params.query);
      if (params?.category) searchParams.append('category', params.category);
      if (params?.country) searchParams.append('country', params.country);
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.pageSize) searchParams.append('page_size', params.pageSize.toString());

      const queryString = searchParams.toString();
      const url = queryString ? `/api/laws?${queryString}` : '/api/laws';

      interface BackendPaginatedResponse {
        data: BackendLawResponse[];
        total: number;
        page: number;
        page_size: number;
        total_pages: number;
      }

      const response = await apiClient.get<BackendPaginatedResponse>(url);
      return {
        data: response.data.data.map(convertLaw),
        total: response.data.total,
        page: response.data.page,
        pageSize: response.data.page_size,
        totalPages: response.data.total_pages,
      };
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * 法令詳細を取得
   * GET /api/laws/:id
   */
  async getLaw(id: string): Promise<Law> {
    try {
      const response = await apiClient.get<BackendLawResponse>(`/api/laws/${id}`);
      return convertLaw(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * 法令の条文一覧を取得
   * GET /api/laws/:id/articles
   */
  async getLawArticles(lawId: string): Promise<LawArticle[]> {
    try {
      const response = await apiClient.get<{ data: BackendLawArticleResponse[] }>(
        `/api/laws/${lawId}/articles`
      );
      return response.data.data.map(convertLawArticle);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * ブックマーク一覧を取得
   * GET /api/laws/bookmarks
   */
  async getBookmarks(): Promise<Bookmark[]> {
    try {
      const response = await apiClient.get<{ data: BackendBookmarkResponse[] }>(
        '/api/laws/bookmarks'
      );
      return response.data.data.map(convertBookmark);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * ブックマークを追加
   * POST /api/laws/bookmarks
   */
  async addBookmark(lawId: string, articleNumber?: string): Promise<Bookmark> {
    try {
      const response = await apiClient.post<BackendBookmarkResponse>('/api/laws/bookmarks', {
        law_id: lawId,
        article_number: articleNumber,
      });
      return convertBookmark(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * ブックマークを削除
   * DELETE /api/laws/bookmarks/:id
   */
  async removeBookmark(bookmarkId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/laws/bookmarks/${bookmarkId}`);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },
};

export default lawService;
